import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signOut, onAuthStateChanged } from "firebase/auth";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
} from "firebase/firestore";
import { auth, db } from "../../Firebase";
import {
  FiLogOut,
  FiEdit2,
  FiTrash2,
  FiUsers,
  FiFileText,
  FiDatabase,
  FiGrid,
  FiPlusCircle,
  FiX,
  FiCheck,
  FiPhone,
  FiMail,
  FiClock,
  FiCalendar,
} from "react-icons/fi";
import Swal from "sweetalert2";
import {
  ADMIN_STORAGE_KEYS,
  loadStoredData,
  saveStoredData,
  clearStoredData,
  getStoredAdminAuth,
} from "../../utils/adminStorage";
import "./AdminDashboard.css";

// Maximum allowed image upload size (500KB for base64 to stay within Firestore limits)
const MAX_IMAGE_SIZE_BYTES = 500 * 1024;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview"); // overview, services, doctors, blogs
  const [, setOfflineMode] = useState(false);

  // Data states
  const [services, setServices] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [aboutStats, setAboutStats] = useState({
    param1Value: "NABH",
    param1Label: "Accredited Standards",
    yearsOfService: 15,
    happyPatients: 50,
    param4Value: "24/7",
    param4Label: "Emergency Support",
  });

  // Loading states for data
  const [fetchingData, setFetchingData] = useState(false);

  // Edit/Create Modal states
  const [modalType, setModalType] = useState(null); // 'service', 'doctor', 'blog'
  const [isEditing, setIsEditing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  // Form states
  const [serviceForm, setServiceForm] = useState({
    name: "",
    description: "",
    icon: "",
    overview: "",
    keyTreatments: "",
    images: "",
  });

  const [doctorForm, setDoctorForm] = useState({
    name: "",
    specialty: "",
    experience: "",
    location: "",
    focus: "",
    gender: "Male",
    languages: "",
    acceptingNewPatients: true,
    conditions: "",
    bio: "",
    image: "",
  });

  const [blogForm, setBlogForm] = useState({
    title: "",
    category: "",
    readTime: "",
    type: "Article",
    image: "",
    description: "",
    content: "",
  });

  // Verify auth state
  useEffect(() => {
    const storedAuth = getStoredAdminAuth();
    if (storedAuth?.fallback) {
      setUser({ email: storedAuth.email });
      setLoading(false);
      setOfflineMode(true);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        setLoading(false);
      } else {
        navigate("/admin/login");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Load database collections
  const fetchData = async () => {
    setFetchingData(true);
    try {
      const storedServices = loadStoredData(ADMIN_STORAGE_KEYS.services, []);
      const storedDoctors = loadStoredData(ADMIN_STORAGE_KEYS.doctors, []);
      const storedBlogs = loadStoredData(ADMIN_STORAGE_KEYS.blogs, []);
      const storedMessages = loadStoredData(ADMIN_STORAGE_KEYS.messages, []);
      const storedAppointments = loadStoredData(
        ADMIN_STORAGE_KEYS.appointments,
        [],
      );
      const storedAboutStats = loadStoredData(
        ADMIN_STORAGE_KEYS.aboutStats,
        aboutStats,
      );

      setServices(storedServices);
      setDoctors(storedDoctors);
      setBlogs(storedBlogs);
      setMessages(
        storedMessages.sort(
          (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0),
        ),
      );
      setAppointments(
        storedAppointments.sort(
          (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0),
        ),
      );
      setAboutStats(storedAboutStats);

      // 1. Fetch Services
      const servicesSnap = await getDocs(collection(db, "services"));
      const servicesList = servicesSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setServices(servicesList);
      saveStoredData(ADMIN_STORAGE_KEYS.services, servicesList);

      // 2. Fetch Doctors
      const doctorsSnap = await getDocs(collection(db, "doctors"));
      const doctorsList = doctorsSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setDoctors(doctorsList);
      saveStoredData(ADMIN_STORAGE_KEYS.doctors, doctorsList);

      // 3. Fetch Blogs
      const blogsSnap = await getDocs(collection(db, "blogs"));
      const blogsList = blogsSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setBlogs(blogsList);
      saveStoredData(ADMIN_STORAGE_KEYS.blogs, blogsList);

      // 4. Fetch Messages
      const messagesSnap = await getDocs(collection(db, "messages"));
      const messagesList = messagesSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      messagesList.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      );
      setMessages(messagesList);
      saveStoredData(ADMIN_STORAGE_KEYS.messages, messagesList);

      // 5. Fetch Appointments
      const appointmentsSnap = await getDocs(collection(db, "appointments"));
      const appointmentsList = appointmentsSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      appointmentsList.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      );
      setAppointments(appointmentsList);
      saveStoredData(ADMIN_STORAGE_KEYS.appointments, appointmentsList);

      // 6. Fetch About Stats
      const statsDoc = await getDoc(doc(db, "settings", "aboutStats"));
      if (statsDoc.exists()) {
        const statsData = statsDoc.data();
        setAboutStats(statsData);
        saveStoredData(ADMIN_STORAGE_KEYS.aboutStats, statsData);
      }
      setOfflineMode(false);
    } catch (err) {
      console.error("Error fetching admin data: ", err);
      setOfflineMode(true);
    } finally {
      setFetchingData(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  // Logout handle
  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.error("Logout error", err);
    } finally {
      clearStoredData(ADMIN_STORAGE_KEYS.auth);
      navigate("/admin/login");
    }
  };

  // Form utilities
  const openCreateModal = (type) => {
    setModalType(type);
    setIsEditing(false);
    setSelectedId(null);
    // Clear selected images when opening create modal
    setSelectedServiceImages(null);
    setSelectedDoctorImage(null);
    setSelectedBlogImage(null);
    if (type === "service") {
      setServiceForm({
        name: "",
        description: "",
        icon: "🩺",
        overview: "",
        keyTreatments: "",
        images: "",
      });
    } else if (type === "doctor") {
      setDoctorForm({
        name: "",
        specialty: "",
        experience: "",
        location: "",
        focus: "",
        gender: "Male",
        languages: "",
        acceptingNewPatients: true,
        conditions: "",
        bio: "",
        image: "/assets/images/Doctor.JPG",
      });
    } else if (type === "blog") {
      setBlogForm({
        title: "",
        category: "",
        readTime: "5 min read",
        type: "Article",
        image:
          "https://images.unsplash.com/photo-1505751172876-fa1923c5c528?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        description: "",
        content: "",
      });
    }
  };

  const openEditModal = (type, item) => {
    setModalType(type);
    setIsEditing(true);
    setSelectedId(item.id);
    // Clear selected images when opening edit modal
    setSelectedServiceImages(null);
    setSelectedDoctorImage(null);
    setSelectedBlogImage(null);

    if (type === "service") {
      setServiceForm({
        name: item.name || "",
        description: item.description || "",
        icon: item.icon || "",
        overview: item.overview || "",
        keyTreatments: Array.isArray(item.keyTreatments)
          ? item.keyTreatments.join("\n")
          : "",
        images: Array.isArray(item.images)
          ? item.images.join("\n")
          : item.images || "",
      });
    } else if (type === "doctor") {
      setDoctorForm({
        name: item.name || "",
        specialty: item.specialty || "",
        experience: item.experience || "",
        location: item.location || "",
        focus: item.focus || "",
        gender: item.gender || "Male",
        languages: Array.isArray(item.languages)
          ? item.languages.join("\n")
          : "",
        acceptingNewPatients: item.acceptingNewPatients !== false,
        conditions: Array.isArray(item.conditions)
          ? item.conditions.join("\n")
          : "",
        bio: item.bio || "",
        image: item.image || "",
      });
    } else if (type === "blog") {
      setBlogForm({
        title: item.title || "",
        category: item.category || "",
        readTime: item.readTime || "",
        type: item.type || "Article",
        image: item.image || "",
        description: item.description || "",
        content: item.content || "",
      });
    }
  };

  const [uploading, setUploading] = useState(false);

  // Convert image file to base64 string
  const convertImageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      if (!file) {
        resolve(null);
        return;
      }
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        reject(
          new Error(
            `"${file.name}" is ${(file.size / (1024 * 1024)).toFixed(2)}MB. Images must be 500KB or smaller.`,
          ),
        );
        return;
      }
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  // Rejects oversized images as soon as they are selected, before upload/save
  const handleImageFileChange = (e) => {
    const files = e.target.files;
    for (let i = 0; i < files.length; i++) {
      if (files[i].size > MAX_IMAGE_SIZE_BYTES) {
        Swal.fire(
          "Image Too Large",
          `"${files[i].name}" is ${(files[i].size / (1024 * 1024)).toFixed(2)}MB. Please choose an image that is 500KB or smaller.`,
          "warning",
        );
        e.target.value = "";
        return;
      }
    }
  };

  // Store selected files in state to prevent them from being lost
  const [selectedServiceImages, setSelectedServiceImages] = useState(null);
  const [selectedDoctorImage, setSelectedDoctorImage] = useState(null);
  const [selectedBlogImage, setSelectedBlogImage] = useState(null);

  // CRUD Submits
  const handleServiceSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      let uploadedImageUrls = [];
      const imageFiles = selectedServiceImages;
      let newImagesUploaded = false;
      if (imageFiles && imageFiles.length > 0) {
        try {
          for (let i = 0; i < imageFiles.length; i++) {
            const base64String = await convertImageToBase64(imageFiles[i]);
            uploadedImageUrls.push(base64String);
          }
          newImagesUploaded = true;
        } catch (imgErr) {
          console.error("Image conversion failed:", imgErr);
          Swal.fire("Error", imgErr.message, "error");
          setUploading(false);
          return;
        }
      }

      let finalImages;
      if (newImagesUploaded) {
        finalImages = uploadedImageUrls;
      } else {
        finalImages = serviceForm.images
          ? serviceForm.images
              .split("\n")
              .map((s) => s.trim())
              .filter(Boolean)
          : [];
      }

      const payload = {
        name: serviceForm.name,
        description: serviceForm.description,
        icon: serviceForm.icon,
        overview: serviceForm.overview,
        keyTreatments: serviceForm.keyTreatments
          ? serviceForm.keyTreatments
              .split("\n")
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
        images: finalImages,
      };

      let updatedServices = [...services];
      if (isEditing) {
        await updateDoc(doc(db, "services", selectedId), payload);
        updatedServices = updatedServices.map((item) =>
          item.id === selectedId
            ? { ...item, ...payload, id: selectedId }
            : item,
        );
      } else {
        let id = serviceForm.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
        if (!id || id === "-") id = "service-" + Date.now();
        await setDoc(doc(db, "services", id), payload);
        updatedServices = [{ id, ...payload }, ...updatedServices];
      }
      setServices(updatedServices);
      saveStoredData(ADMIN_STORAGE_KEYS.services, updatedServices);
      setModalType(null);
      setSelectedServiceImages(null);
      Swal.fire(
        "Success",
        isEditing
          ? "Service updated successfully!"
          : "Service created successfully!",
        "success",
      );
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Error saving service: " + err.message, "error");
    } finally {
      setUploading(false);
    }
  };

  const handleDoctorSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      let finalImageUrl = doctorForm.image;
      const imageFile = selectedDoctorImage;
      if (imageFile) {
        try {
          finalImageUrl = await convertImageToBase64(imageFile);
        } catch (imgErr) {
          console.error("Image conversion failed:", imgErr);
          Swal.fire("Error", imgErr.message, "error");
          setUploading(false);
          return;
        }
      }

      const payload = {
        name: doctorForm.name,
        specialty: doctorForm.specialty,
        experience: doctorForm.experience,
        location: doctorForm.location,
        focus: doctorForm.focus,
        gender: doctorForm.gender,
        languages: doctorForm.languages
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
        acceptingNewPatients: doctorForm.acceptingNewPatients,
        conditions: doctorForm.conditions
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
        bio: doctorForm.bio,
        image: finalImageUrl,
      };

      let updatedDoctors = [...doctors];
      if (isEditing) {
        await updateDoc(doc(db, "doctors", selectedId), payload);
        updatedDoctors = updatedDoctors.map((item) =>
          item.id === selectedId
            ? { ...item, ...payload, id: selectedId }
            : item,
        );
      } else {
        const newDoctorId = `doctor-${Date.now()}`;
        await addDoc(collection(db, "doctors"), payload);
        updatedDoctors = [{ id: newDoctorId, ...payload }, ...updatedDoctors];
      }
      setDoctors(updatedDoctors);
      saveStoredData(ADMIN_STORAGE_KEYS.doctors, updatedDoctors);
      setModalType(null);
      setSelectedDoctorImage(null);
      Swal.fire(
        "Success",
        isEditing
          ? "Doctor updated successfully!"
          : "Doctor created successfully!",
        "success",
      );
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Error saving doctor: " + err.message, "error");
    } finally {
      setUploading(false);
    }
  };

  const handleBlogSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      let finalImageUrl = blogForm.image;
      const imageFile = selectedBlogImage;
      if (imageFile) {
        try {
          finalImageUrl = await convertImageToBase64(imageFile);
        } catch (imgErr) {
          console.error("Image conversion failed:", imgErr);
          Swal.fire("Error", imgErr.message, "error");
          setUploading(false);
          return;
        }
      }

      const payload = {
        title: blogForm.title,
        category: blogForm.category,
        readTime: blogForm.readTime,
        type: blogForm.type,
        image: finalImageUrl,
        description: blogForm.description,
        content: blogForm.content,
      };

      let updatedBlogs = [...blogs];
      if (isEditing) {
        await updateDoc(doc(db, "blogs", selectedId), payload);
        updatedBlogs = updatedBlogs.map((item) =>
          item.id === selectedId
            ? { ...item, ...payload, id: selectedId }
            : item,
        );
      } else {
        const newBlogId = `blog-${Date.now()}`;
        await addDoc(collection(db, "blogs"), payload);
        updatedBlogs = [{ id: newBlogId, ...payload }, ...updatedBlogs];
      }
      setBlogs(updatedBlogs);
      saveStoredData(ADMIN_STORAGE_KEYS.blogs, updatedBlogs);
      setModalType(null);
      setSelectedBlogImage(null);
      Swal.fire(
        "Success",
        isEditing
          ? "Blog updated successfully!"
          : "Blog published successfully!",
        "success",
      );
    } catch (err) {
      console.error("Blog form error: ", err);
      Swal.fire("Error", "Error saving blog: " + err.message, "error");
    } finally {
      setUploading(false);
    }
  };

  const handleStatsUpdate = async (e) => {
    e.preventDefault();
    try {
      await setDoc(doc(db, "settings", "aboutStats"), aboutStats);
      saveStoredData(ADMIN_STORAGE_KEYS.aboutStats, aboutStats);
      Swal.fire("Success", "About Us stats updated successfully!", "success");
    } catch (err) {
      console.error("Error updating stats:", err);
      Swal.fire("Error", "Failed to update stats: " + err.message, "error");
    }
  };

  // CRUD Delete
  const handleDeleteItem = async (type, id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You want to delete this ${type}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      const collectionName =
        type === "service"
          ? "services"
          : type === "doctor"
            ? "doctors"
            : "blogs";
      await deleteDoc(doc(db, collectionName, id));
      if (type === "service") {
        const nextServices = services.filter((item) => item.id !== id);
        setServices(nextServices);
        saveStoredData(ADMIN_STORAGE_KEYS.services, nextServices);
      } else if (type === "doctor") {
        const nextDoctors = doctors.filter((item) => item.id !== id);
        setDoctors(nextDoctors);
        saveStoredData(ADMIN_STORAGE_KEYS.doctors, nextDoctors);
      } else {
        const nextBlogs = blogs.filter((item) => item.id !== id);
        setBlogs(nextBlogs);
        saveStoredData(ADMIN_STORAGE_KEYS.blogs, nextBlogs);
      }
      Swal.fire("Deleted!", `The ${type} has been deleted.`, "success");
    } catch (err) {
      console.error(err);
      Swal.fire("Error", `Error deleting ${type}: ` + err.message, "error");
    }
  };

  if (loading) {
    return (
      <div className="admin-loading-screen">
        <div className="admin-spinner"></div>
        <p>Loading Admin Dashboard...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-container">
      {/* Sidebar Navigation */}
      <aside className="admin-sidebar">
        <div className="sidebar-brand">
          <FiDatabase className="brand-db-icon" />
          <span>Swarup Admin</span>
        </div>
        <nav className="sidebar-nav">
          <button
            className={`nav-item ${activeTab === "overview" ? "active" : ""}`}
            onClick={() => setActiveTab("overview")}
          >
            <FiGrid /> Overview
          </button>
          <button
            className={`nav-item ${activeTab === "services" ? "active" : ""}`}
            onClick={() => setActiveTab("services")}
          >
            <FiDatabase /> Manage Services
          </button>
          <button
            className={`nav-item ${activeTab === "doctors" ? "active" : ""}`}
            onClick={() => setActiveTab("doctors")}
          >
            <FiUsers /> Manage Doctors
          </button>
          <button
            className={`nav-item ${activeTab === "blogs" ? "active" : ""}`}
            onClick={() => setActiveTab("blogs")}
          >
            <FiFileText /> Manage Blogs
          </button>
          <button
            className={`nav-item ${activeTab === "messages" ? "active" : ""}`}
            onClick={() => setActiveTab("messages")}
          >
            <FiLogOut /> Messages
          </button>
          <button
            className={`nav-item ${activeTab === "appointments" ? "active" : ""}`}
            onClick={() => setActiveTab("appointments")}
          >
            <FiUsers /> Appointments
          </button>
        </nav>
        <div className="sidebar-footer">
          <div className="user-profile-info">
            <span className="user-email">{user.email}</span>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            <FiLogOut /> Log Out
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="admin-main-viewport">
        {/* Top Header */}
        <header className="viewport-header">
          <h2>
            {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Management
          </h2>
          <div className="header-actions">
            {activeTab !== "overview" &&
              activeTab !== "messages" &&
              activeTab !== "appointments" && (
                <button
                  className="add-new-btn-top"
                  onClick={() => openCreateModal(activeTab.slice(0, -1))}
                >
                  <FiPlusCircle /> Add New {activeTab.slice(0, -1)}
                </button>
              )}
          </div>
        </header>

        <div className="viewport-body">
          {fetchingData && (
            <div className="fetching-indicator">
              <span className="spinner-small"></span> Syncing with Firebase...
            </div>
          )}

          {/* TAB 1: OVERVIEW */}
          {activeTab === "overview" && (
            <div className="overview-tab">
              <div className="welcome-banner">
                <h3>Welcome back, Administrator</h3>
                <p>
                  Monitor your clinical offerings, team composition, and
                  wellness publications here.
                </p>
              </div>

              <div className="stats-cards-grid">
                <div
                  className="stat-summary-card"
                  onClick={() => setActiveTab("services")}
                >
                  <div className="stat-icon-wrapper blue">
                    <FiGrid />
                  </div>
                  <div className="stat-data">
                    <span className="stat-num">{services.length}</span>
                    <span className="stat-lbl">Services / Departments</span>
                  </div>
                </div>

                <div
                  className="stat-summary-card"
                  onClick={() => setActiveTab("doctors")}
                >
                  <div className="stat-icon-wrapper green">
                    <FiUsers />
                  </div>
                  <div className="stat-data">
                    <span className="stat-num">{doctors.length}</span>
                    <span className="stat-lbl">Active Doctors</span>
                  </div>
                </div>

                <div
                  className="stat-summary-card"
                  onClick={() => setActiveTab("blogs")}
                >
                  <div className="stat-icon-wrapper orange">
                    <FiFileText />
                  </div>
                  <div className="stat-data">
                    <span className="stat-num">{blogs.length}</span>
                    <span className="stat-lbl">Blog Articles</span>
                  </div>
                </div>

                <div
                  className="stat-summary-card"
                  onClick={() => setActiveTab("messages")}
                >
                  <div
                    className="stat-icon-wrapper"
                    style={{ color: "purple" }}
                  >
                    <FiFileText />
                  </div>
                  <div className="stat-data">
                    <span className="stat-num">{messages.length}</span>
                    <span className="stat-lbl">Messages</span>
                  </div>
                </div>

                <div
                  className="stat-summary-card"
                  onClick={() => setActiveTab("appointments")}
                >
                  <div className="stat-icon-wrapper" style={{ color: "teal" }}>
                    <FiUsers />
                  </div>
                  <div className="stat-data">
                    <span className="stat-num">{appointments.length}</span>
                    <span className="stat-lbl">Appointments</span>
                  </div>
                </div>
              </div>

              <div className="system-status-box">
                <h4>System Configurations</h4>
                <p>
                  All hospital page data is now successfully integrated with
                  Google Firestore. Any changes made through this portal are
                  instantly synchronized with the live website views.
                </p>
                <h4 style={{ marginTop: "30px" }}>About Us Stats</h4>
                <form
                  onSubmit={handleStatsUpdate}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                    marginTop: "15px",
                    maxWidth: "600px",
                    background: "#f8fafc",
                    padding: "20px",
                    borderRadius: "12px",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "15px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: "0.9rem",
                          marginBottom: "5px",
                          fontWeight: "bold",
                        }}
                      >
                        Parameter 1 Value
                      </label>
                      <input
                        type="text"
                        value={aboutStats.param1Value || ""}
                        onChange={(e) =>
                          setAboutStats({
                            ...aboutStats,
                            param1Value: e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #cbd5e1",
                          borderRadius: "6px",
                        }}
                        placeholder="e.g. NABH"
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: "0.9rem",
                          marginBottom: "5px",
                          fontWeight: "bold",
                        }}
                      >
                        Parameter 1 Label
                      </label>
                      <input
                        type="text"
                        value={aboutStats.param1Label || ""}
                        onChange={(e) =>
                          setAboutStats({
                            ...aboutStats,
                            param1Label: e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #cbd5e1",
                          borderRadius: "6px",
                        }}
                        placeholder="e.g. Accredited Standards"
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "15px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: "0.9rem",
                          marginBottom: "5px",
                          fontWeight: "bold",
                        }}
                      >
                        Years of Service (Number)
                      </label>
                      <input
                        type="number"
                        value={aboutStats.yearsOfService}
                        onChange={(e) =>
                          setAboutStats({
                            ...aboutStats,
                            yearsOfService: Number(e.target.value),
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #cbd5e1",
                          borderRadius: "6px",
                        }}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: "0.9rem",
                          marginBottom: "5px",
                          fontWeight: "bold",
                        }}
                      >
                        Happy Patients (in K)
                      </label>
                      <input
                        type="number"
                        value={aboutStats.happyPatients}
                        onChange={(e) =>
                          setAboutStats({
                            ...aboutStats,
                            happyPatients: Number(e.target.value),
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #cbd5e1",
                          borderRadius: "6px",
                        }}
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "15px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: "0.9rem",
                          marginBottom: "5px",
                          fontWeight: "bold",
                        }}
                      >
                        Parameter 4 Value
                      </label>
                      <input
                        type="text"
                        value={aboutStats.param4Value || ""}
                        onChange={(e) =>
                          setAboutStats({
                            ...aboutStats,
                            param4Value: e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #cbd5e1",
                          borderRadius: "6px",
                        }}
                        placeholder="e.g. 24/7"
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: "0.9rem",
                          marginBottom: "5px",
                          fontWeight: "bold",
                        }}
                      >
                        Parameter 4 Label
                      </label>
                      <input
                        type="text"
                        value={aboutStats.param4Label || ""}
                        onChange={(e) =>
                          setAboutStats({
                            ...aboutStats,
                            param4Label: e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #cbd5e1",
                          borderRadius: "6px",
                        }}
                        placeholder="e.g. Emergency Support"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    style={{
                      padding: "10px 20px",
                      background: "var(--color-primary)",
                      color: "#fff",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontWeight: "bold",
                      alignSelf: "flex-start",
                    }}
                  >
                    Update All Stats
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* TAB 2: MANAGE SERVICES */}
          {activeTab === "services" && (
            <div className="data-table-container">
              <div className="items-list-grid">
                {services.map((service) => (
                  <div key={service.id} className="data-list-card">
                    <div className="card-top">
                      <span className="card-icon-display">{service.icon}</span>
                      <div className="card-meta">
                        <h4>{service.name}</h4>
                        <p>{service.description}</p>
                      </div>
                    </div>
                    <div className="card-actions">
                      <button
                        className="action-edit"
                        onClick={() => openEditModal("service", service)}
                      >
                        <FiEdit2 /> Edit
                      </button>
                      <button
                        className="action-delete"
                        onClick={() => handleDeleteItem("service", service.id)}
                      >
                        <FiTrash2 /> Delete
                      </button>
                    </div>
                  </div>
                ))}
                {services.length === 0 && (
                  <p className="no-items-text">
                    No services found. Add one above or seed default data.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* TAB 3: MANAGE DOCTORS */}
          {activeTab === "doctors" && (
            <div className="data-table-container">
              <div className="items-list-grid">
                {doctors.map((doctor) => (
                  <div
                    key={doctor.id}
                    className="data-list-card doctor-card-admin"
                  >
                    <div className="card-top">
                      <div className="doctor-img-box-admin">
                        <img src={doctor.image} alt={doctor.name} />
                      </div>
                      <div className="card-meta">
                        <h4>{doctor.name}</h4>
                        <span className="specialty-badge-admin">
                          {doctor.specialty}
                        </span>
                        <p>
                          {doctor.experience} • {doctor.location}
                        </p>
                      </div>
                    </div>
                    <div className="card-actions">
                      <button
                        className="action-edit"
                        onClick={() => openEditModal("doctor", doctor)}
                      >
                        <FiEdit2 /> Edit
                      </button>
                      <button
                        className="action-delete"
                        onClick={() => handleDeleteItem("doctor", doctor.id)}
                      >
                        <FiTrash2 /> Delete
                      </button>
                    </div>
                  </div>
                ))}
                {doctors.length === 0 && (
                  <p className="no-items-text">
                    No doctors found. Add one above or seed default data.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* TAB 4: MANAGE BLOGS */}
          {activeTab === "blogs" && (
            <div className="data-table-container">
              <div className="items-list-grid">
                {blogs.map((blog) => (
                  <div key={blog.id} className="data-list-card blog-card-admin">
                    <div className="card-top">
                      <div className="blog-img-box-admin">
                        <img src={blog.image} alt={blog.title} />
                      </div>
                      <div className="card-meta">
                        <h4>{blog.title}</h4>
                        <span className="blog-category-badge">
                          {blog.category}
                        </span>
                        <p>
                          {blog.type} • {blog.readTime}
                        </p>
                      </div>
                    </div>
                    <div className="card-actions">
                      <button
                        className="action-edit"
                        onClick={() => openEditModal("blog", blog)}
                      >
                        <FiEdit2 /> Edit
                      </button>
                      <button
                        className="action-delete"
                        onClick={() => handleDeleteItem("blog", blog.id)}
                      >
                        <FiTrash2 /> Delete
                      </button>
                    </div>
                  </div>
                ))}
                {blogs.length === 0 && (
                  <p className="no-items-text">
                    No blog posts found. Add one above or seed default data.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* TAB 5: MESSAGES */}
          {activeTab === "messages" && (
            <div className="data-table-container">
              <div
                className="items-list-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: "20px",
                }}
              >
                {messages.map((msg) => (
                  <div key={msg.id} className="premium-admin-card">
                    <div className="pac-header">
                      <div className="pac-title-group">
                        <h4>
                          {msg.firstName} {msg.lastName}
                        </h4>
                        <div className="pac-contact-info">
                          <a
                            href={`mailto:${msg.email}`}
                            className="pac-contact-item"
                          >
                            <FiMail /> {msg.email}
                          </a>
                          {msg.phone && (
                            <a
                              href={`tel:${msg.phone}`}
                              className="pac-contact-item"
                            >
                              <FiPhone /> {msg.phone}
                            </a>
                          )}
                        </div>
                      </div>
                      <div className="pac-date">
                        <FiClock /> {new Date(msg.createdAt).toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <span className="pac-badge">{msg.subject}</span>
                    </div>
                    <div className="pac-body">{msg.message}</div>
                  </div>
                ))}
                {messages.length === 0 && (
                  <p className="no-items-text">No messages received yet.</p>
                )}
              </div>
            </div>
          )}

          {/* TAB 6: APPOINTMENTS */}
          {activeTab === "appointments" && (
            <div className="data-table-container">
              <div
                className="items-list-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: "20px",
                }}
              >
                {appointments.map((apt) => (
                  <div key={apt.id} className="premium-admin-card">
                    <div className="pac-header">
                      <div className="pac-title-group">
                        <h4>
                          {apt.firstName} {apt.lastName}
                        </h4>
                        <div className="pac-contact-info">
                          <a
                            href={`mailto:${apt.email}`}
                            className="pac-contact-item"
                          >
                            <FiMail /> {apt.email}
                          </a>
                          <a
                            href={`tel:${apt.phone}`}
                            className="pac-contact-item"
                          >
                            <FiPhone /> {apt.phone}
                          </a>
                        </div>
                      </div>
                      <div className="pac-date">
                        <FiClock /> Created:{" "}
                        {new Date(apt.createdAt).toLocaleString()}
                      </div>
                    </div>

                    <div className="pac-meta-grid">
                      <div className="pac-meta-item">
                        <span className="pac-meta-label">Appointment Time</span>
                        <span
                          className="pac-meta-value"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            color: "#016f6b",
                          }}
                        >
                          <FiCalendar /> {apt.date} at {apt.time}
                        </span>
                      </div>
                      <div className="pac-meta-item">
                        <span className="pac-meta-label">Department</span>
                        <span className="pac-meta-value">{apt.department}</span>
                      </div>
                      <div className="pac-meta-item">
                        <span className="pac-meta-label">
                          Doctor Preference
                        </span>
                        <span className="pac-meta-value">
                          {apt.doctor || "Any Available"}
                        </span>
                      </div>
                      <div className="pac-meta-item">
                        <span className="pac-meta-label">Patient Details</span>
                        <span className="pac-meta-value">
                          {apt.gender}, DOB: {apt.dob}
                        </span>
                      </div>
                    </div>

                    <div className="pac-body" style={{ marginTop: "0" }}>
                      <div
                        className="pac-meta-label"
                        style={{ marginBottom: "6px" }}
                      >
                        Reason / Symptoms
                      </div>
                      {apt.reason}
                    </div>
                  </div>
                ))}
                {appointments.length === 0 && (
                  <p className="no-items-text">No appointments booked yet.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* DYNAMIC MODALS FOR CREATE / EDIT */}
      {modalType && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-body">
            <div className="modal-header">
              <h3>
                {isEditing ? "Edit" : "Create New"}{" "}
                {modalType.charAt(0).toUpperCase() + modalType.slice(1)}
              </h3>
              <button
                className="modal-close-btn"
                onClick={() => setModalType(null)}
              >
                <FiX />
              </button>
            </div>

            {/* SERVICE FORM */}
            {modalType === "service" && (
              <form onSubmit={handleServiceSubmit} className="modal-form">
                <div className="form-row">
                  <div className="form-group col-8">
                    <label>Service/Department Name</label>
                    <input
                      type="text"
                      required
                      value={serviceForm.name}
                      onChange={(e) =>
                        setServiceForm({ ...serviceForm, name: e.target.value })
                      }
                    />
                  </div>
                  <div className="form-group col-4">
                    <label>Icon Symbol</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. 🩺, 🦴, ❤️"
                      value={serviceForm.icon}
                      onChange={(e) =>
                        setServiceForm({ ...serviceForm, icon: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Short Description</label>
                  <input
                    type="text"
                    required
                    value={serviceForm.description}
                    onChange={(e) =>
                      setServiceForm({
                        ...serviceForm,
                        description: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Overview Statement</label>
                  <textarea
                    rows={4}
                    required
                    value={serviceForm.overview}
                    onChange={(e) =>
                      setServiceForm({
                        ...serviceForm,
                        overview: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>
                    Key Treatments (Enter one treatment option per line)
                  </label>
                  <textarea
                    rows={4}
                    placeholder="Treatment 1&#10;Treatment 2&#10;Treatment 3"
                    value={serviceForm.keyTreatments}
                    onChange={(e) =>
                      setServiceForm({
                        ...serviceForm,
                        keyTreatments: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Service Images (Upload Multiple Images)</label>
                  <input
                    type="file"
                    id="service-image-upload"
                    multiple
                    accept="image/*"
                    onChange={(e) => {
                      handleImageFileChange(e);
                      setSelectedServiceImages(e.target.files);
                    }}
                  />
                  <small style={{ color: "#64748b" }}>
                    Max file size: 500KB per image.
                  </small>
                  {isEditing && serviceForm.images && (
                    <small style={{ color: "#64748b" }}>
                      Leave empty to keep existing images.
                    </small>
                  )}
                </div>

                <button
                  type="submit"
                  className="modal-submit-btn"
                  disabled={uploading}
                >
                  {uploading ? (
                    "Uploading & Saving..."
                  ) : (
                    <>
                      <FiCheck />{" "}
                      {isEditing ? "Update Service" : "Create Service"}
                    </>
                  )}
                </button>
              </form>
            )}

            {/* DOCTOR FORM */}
            {modalType === "doctor" && (
              <form onSubmit={handleDoctorSubmit} className="modal-form">
                <div className="form-row">
                  <div className="form-group col-6">
                    <label>Doctor Name</label>
                    <input
                      type="text"
                      required
                      value={doctorForm.name}
                      onChange={(e) =>
                        setDoctorForm({ ...doctorForm, name: e.target.value })
                      }
                    />
                  </div>
                  <div className="form-group col-6">
                    <label>Specialty</label>
                    <input
                      type="text"
                      required
                      value={doctorForm.specialty}
                      onChange={(e) =>
                        setDoctorForm({
                          ...doctorForm,
                          specialty: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group col-6">
                    <label>Experience Statement</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. 15+ years experience"
                      value={doctorForm.experience}
                      onChange={(e) =>
                        setDoctorForm({
                          ...doctorForm,
                          experience: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="form-group col-6">
                    <label>Location Clinic</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Main Campus, Joint Care"
                      value={doctorForm.location}
                      onChange={(e) =>
                        setDoctorForm({
                          ...doctorForm,
                          location: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group col-6">
                    <label>Focus Statement</label>
                    <input
                      type="text"
                      required
                      value={doctorForm.focus}
                      onChange={(e) =>
                        setDoctorForm({ ...doctorForm, focus: e.target.value })
                      }
                    />
                  </div>
                  <div className="form-group col-3">
                    <label>Gender</label>
                    <select
                      value={doctorForm.gender}
                      onChange={(e) =>
                        setDoctorForm({ ...doctorForm, gender: e.target.value })
                      }
                    >
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>
                  <div className="form-group col-3">
                    <label className="checkbox-label-container">
                      <input
                        type="checkbox"
                        checked={doctorForm.acceptingNewPatients}
                        onChange={(e) =>
                          setDoctorForm({
                            ...doctorForm,
                            acceptingNewPatients: e.target.checked,
                          })
                        }
                      />
                      <span>Accept Patients</span>
                    </label>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group col-6">
                    <label>Languages (One per line)</label>
                    <textarea
                      rows={3}
                      required
                      placeholder="English&#10;Hindi&#10;Marathi"
                      value={doctorForm.languages}
                      onChange={(e) =>
                        setDoctorForm({
                          ...doctorForm,
                          languages: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="form-group col-6">
                    <label>Treatable Conditions (One per line)</label>
                    <textarea
                      rows={3}
                      required
                      placeholder="Knee pain&#10;Sports injuries"
                      value={doctorForm.conditions}
                      onChange={(e) =>
                        setDoctorForm({
                          ...doctorForm,
                          conditions: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Bio Overview</label>
                  <textarea
                    rows={3}
                    required
                    value={doctorForm.bio}
                    onChange={(e) =>
                      setDoctorForm({ ...doctorForm, bio: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Doctor Photo (Upload Image)</label>
                  <input
                    type="file"
                    id="doctor-image-upload"
                    accept="image/*"
                    onChange={(e) => {
                      handleImageFileChange(e);
                      setSelectedDoctorImage(e.target.files[0]);
                    }}
                  />
                  <small style={{ color: "#64748b" }}>
                    Max file size: 500KB.
                  </small>
                  {isEditing && doctorForm.image && (
                    <div style={{ marginTop: "10px" }}>
                      <img
                        src={doctorForm.image}
                        alt="Current"
                        style={{
                          height: "60px",
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0",
                        }}
                      />
                      <small
                        style={{
                          display: "block",
                          color: "#64748b",
                          marginTop: "4px",
                        }}
                      >
                        Leave empty to keep existing image.
                      </small>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  className="modal-submit-btn"
                  disabled={uploading}
                >
                  {uploading ? (
                    "Uploading & Saving..."
                  ) : (
                    <>
                      <FiCheck />{" "}
                      {isEditing ? "Update Doctor" : "Create Doctor"}
                    </>
                  )}
                </button>
              </form>
            )}

            {/* BLOG FORM */}
            {modalType === "blog" && (
              <form onSubmit={handleBlogSubmit} className="modal-form">
                <div className="form-row">
                  <div className="form-group col-8">
                    <label>Article Title</label>
                    <input
                      type="text"
                      required
                      value={blogForm.title}
                      onChange={(e) =>
                        setBlogForm({ ...blogForm, title: e.target.value })
                      }
                    />
                  </div>
                  <div className="form-group col-4">
                    <label>Category</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Heart Care, Healthy Living"
                      value={blogForm.category}
                      onChange={(e) =>
                        setBlogForm({ ...blogForm, category: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group col-6">
                    <label>Read Duration</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. 5 min read"
                      value={blogForm.readTime}
                      onChange={(e) =>
                        setBlogForm({ ...blogForm, readTime: e.target.value })
                      }
                    />
                  </div>
                  <div className="form-group col-6">
                    <label>Blog Category</label>
                    <select
                      value={blogForm.type}
                      onChange={(e) =>
                        setBlogForm({ ...blogForm, type: e.target.value })
                      }
                    >
                      <option value="Article">Blogs</option>
                      <option value="Case Study">Case Studies</option>
                      <option value="Service">Services</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Blog Cover Image (Upload Image)</label>
                  <input
                    type="file"
                    id="blog-image-upload"
                    accept="image/*"
                    onChange={(e) => {
                      handleImageFileChange(e);
                      setSelectedBlogImage(e.target.files[0]);
                    }}
                  />
                  <small style={{ color: "#64748b" }}>
                    Max file size: 500KB.
                  </small>
                  {isEditing && blogForm.image && (
                    <div style={{ marginTop: "10px" }}>
                      <img
                        src={blogForm.image}
                        alt="Current"
                        style={{
                          height: "60px",
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0",
                        }}
                      />
                      <small
                        style={{
                          display: "block",
                          color: "#64748b",
                          marginTop: "4px",
                        }}
                      >
                        Leave empty to keep existing image.
                      </small>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Content Summary</label>
                  <textarea
                    rows={2}
                    required
                    value={blogForm.description}
                    onChange={(e) =>
                      setBlogForm({ ...blogForm, description: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>
                    Full Content (Supports plain text or custom HTML tags for
                    formatting)
                  </label>
                  <textarea
                    rows={8}
                    placeholder="<p>Paragraph content</p>&#10;<h4>Section Heading</h4>&#10;<p>Another paragraph</p>"
                    value={blogForm.content}
                    onChange={(e) =>
                      setBlogForm({ ...blogForm, content: e.target.value })
                    }
                  />
                </div>

                <button
                  type="submit"
                  className="modal-submit-btn"
                  disabled={uploading}
                >
                  {uploading ? (
                    "Uploading & Saving..."
                  ) : (
                    <>
                      <FiCheck />{" "}
                      {isEditing ? "Update Article" : "Publish Article"}
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
